import type { ComponentType } from 'react'
import type { CatalogEntry } from '@kayd/shared'
import { useInView } from '../hooks/useEnvironment'

interface PlateProps {
  game: CatalogEntry
  /** Supplied once the three.js chunk has loaded. Absent means no preview. */
  GameView?: ComponentType<{ previewKey: string; active: boolean; visible: boolean }> | undefined
  /** True only after the shared renderer has actually been created. */
  stageReady: boolean
  /** False under prefers-reduced-motion. The scene renders, it just holds still. */
  animate: boolean
  /** False while plates carry no artwork. The frame is not rendered at all. */
  showMedia: boolean
  /**
   * Position in the visible catalog, 1-based. Not the manifest's `no`, which
   * is a permanent identity and leaves gaps whenever a game is hidden.
   */
  position: number
}

export function Plate({ game, GameView, stageReady, animate, showMedia, position }: PlateProps) {
  const [frameRef, inView] = useInView<HTMLDivElement>('300px')

  return (
    <a className={showMedia ? 'plate' : 'plate plate--bare'} href={game.path}>
      <span className="plate__tick" aria-hidden="true">
        {String(position).padStart(2, '0')}
      </span>

      {showMedia ? (
        <div className="plate__frame" ref={frameRef}>
          {/*
            The canvas is transparent, so a fallback left underneath would read
            through the scene. It is dropped only once the renderer exists, which
            means a canvas that never initialises still leaves a labelled frame.
          */}
          {stageReady ? null : (
            <span className="plate__fallback" aria-hidden="true">
              {game.title}
            </span>
          )}
          {GameView ? (
            <GameView previewKey={game.preview} active={inView && animate} visible={inView} />
          ) : null}
        </div>
      ) : null}

      <div className="plate__body">
        <h2 className="plate__title">{game.title}</h2>
        <p className="plate__blurb">{game.blurb}</p>
        <div className="plate__meta">
          <ul className="plate__tags">
            {game.tags.map((tag) => (
              <li key={tag}>{tag}</li>
            ))}
          </ul>
          <span>{game.year}</span>
          {game.status === 'wip' ? <span className="plate__wip">in progress</span> : null}
        </div>
        <p className="plate__go">
          <span>Play {game.title}</span>
        </p>
      </div>
    </a>
  )
}
